import type {
	IMoodBoardImage,
	TMoodBoardScene,
} from 'api/resources/moodBoards';
import type { TExcalidrawFiles } from '../types/excalidraw';

// The parts of a scene element this module reads. Excalidraw's element union
// only carries `fileId` on its image members, so it is narrowed here rather
// than pulled from the package's types.
type TSceneImageElement = {
	type?: string;
	isDeleted?: boolean;
	fileId?: string | null;
};

// The Excalidraw file ids referenced by the scene's live (non-deleted) image
// elements — the images the board actually draws.
const getReferencedFileIds = (elements: unknown): string[] => {
	const fileIds = new Set<string>();

	for (const element of (elements ?? []) as TSceneImageElement[]) {
		if (element?.type === 'image' && !element.isDeleted && element.fileId) {
			fileIds.add(element.fileId);
		}
	}

	return Array.from(fileIds);
};

// The file extension -> mime type pairs a board can hold, used to type a file
// entry rebuilt from an upload (the board json doesn't store the mime type
// separately). Anything else falls back to PNG, which Excalidraw only uses as
// a hint when drawing the already-decoded image.
const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
	gif: 'image/gif',
	jpeg: 'image/jpeg',
	jpg: 'image/jpeg',
	png: 'image/png',
	svg: 'image/svg+xml',
	webp: 'image/webp',
};

const getMimeType = (fileName: string): string =>
	MIME_TYPE_BY_EXTENSION[fileName.split('.').pop()?.toLowerCase() ?? ''] ??
	'image/png';

// The design manager names each upload after the Excalidraw file id it stores,
// so an upload can be matched back to its scene file by name alone. This is
// what recovers a board whose stored scene lost its `files` (the editor's
// serializer used to drop them), where `moodBoardFiles` is empty and the file
// entries are gone entirely.
const getUploadedFileId = (image: IMoodBoardImage): string =>
	image.fileName.replace(/\.[^.]*$/, '');

// Points each scene image at the URL the BE currently serves for it, looked up
// in the board's `images` through the scene's `moodBoardFiles` map (Excalidraw
// file id -> BE fileId), falling back to matching the upload by its file name.
// A file entry the stored scene is missing altogether is rebuilt from the
// matched upload, so a board saved without its `files` still renders its
// images. Files with no matching upload keep whatever URL the scene persisted:
// an image whose upload fell back to an inline data URL. Read-only counterpart
// of the design manager's resolver: the portal never uploads, so there is no
// upload cache to seed.
export const resolveSceneImages = (
	scene: TMoodBoardScene | null,
	images: IMoodBoardImage[],
): TMoodBoardScene | null => {
	if (!scene) {
		return scene;
	}

	const imagesByFileId = new Map(images.map((image) => [image.fileId, image]));
	// Later uploads win: the same image re-uploaded keeps the newest URL.
	const imagesByName = new Map(
		images.map((image) => [getUploadedFileId(image), image]),
	);
	const files: TExcalidrawFiles = { ...(scene.files ?? {}) };
	// Every file the scene stores, plus every file its elements reference — the
	// latter covers entries the stored scene lost.
	const fileIds = Array.from(
		new Set([...Object.keys(files), ...getReferencedFileIds(scene.elements)]),
	);

	for (const fileId of fileIds) {
		const image =
			imagesByFileId.get(scene.moodBoardFiles?.[fileId] ?? '') ??
			imagesByName.get(fileId);

		if (!image) {
			continue;
		}

		const file = files[fileId];

		files[fileId] = file
			? { ...file, dataURL: image.original as typeof file.dataURL }
			: ({
					id: fileId,
					dataURL: image.original,
					mimeType: getMimeType(image.fileName),
					created: Date.now(),
				} as TExcalidrawFiles[string]);
	}

	return { ...scene, files };
};
